import { useMemo } from 'react';
import {
  extractSettingAs,
  GRADEBOOK_SETTINGS,
} from '@macmillan-learning/gradebook-shared';

/**
 * Hook to extract and memoize gradebook settings from Redux state
 *
 * Extracts commonly used settings from the rdxGradebookSettings array
 * and provides sensible defaults when settings are not configured.
 *
 * @param {Array} rdxGradebookSettings - Gradebook settings array from Redux
 * @returns {Object} Extracted settings with defaults
 */
export function useGradebookSettings(rdxGradebookSettings) {
  return useMemo(() => {
    // Extract roundPercentType for grade formatting precision
    // Controls decimal places in percentage display (whole, tenths, hundredths)
    const roundPercentType =
      extractSettingAs(rdxGradebookSettings, 'roundPercentType', (x) =>
        Number(x.value),
      ) ?? GRADEBOOK_SETTINGS.roundPercentWhole;

    // Extract gradingType for category weight display
    // Determines if weights are shown and how categories contribute to total
    const gradingType =
      extractSettingAs(rdxGradebookSettings, 'gradingType', (x) =>
        Number(x.value),
      ) ?? GRADEBOOK_SETTINGS.gradingTypeStandard;

    // Extract requireStudentId setting to conditionally show Student ID column
    // When true, displays a column for third-party student IDs
    const requireStudentId =
      extractSettingAs(
        rdxGradebookSettings,
        'requireStudentId',
        (x) => x.value,
      ) === 'true';

    // Extract gradesToReport setting for due date visibility
    // Controls whether grades for not-yet-due assignments are shown
    const gradesToReport = extractSettingAs(
      rdxGradebookSettings,
      'gradesToReport',
      (x) => Number(x.value),
    );

    return {
      roundPercentType,
      gradingType,
      requireStudentId,
      gradesToReport,
    };
  }, [rdxGradebookSettings]);
}

export default useGradebookSettings;
